import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSchedule } from '../api';
import type { EnrichedMatch } from '../types';
import { rigaTime } from '../format';

export default function LiveBanner() {
  const { data } = useQuery({ queryKey: ['schedule'], queryFn: getSchedule, refetchInterval: 60_000 });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const live = useMemo(() => (data?.matches ?? []).filter((m) => m.status === 'in'), [data]);
  const next = useMemo(() => {
    const pre = (data?.matches ?? []).filter((m) => m.status === 'pre').sort((a, b) => a.utc_kickoff.localeCompare(b.utc_kickoff));
    return pre[0] ?? null;
  }, [data]);

  if (live.length === 0 && !next) return null;

  return (
    <div className="px-4 lg:px-0 mb-3">
      {live.length > 0 ? (
        <div className="space-y-1.5">
          {live.slice(0, 3).map((m) => <LiveRow key={m.id} m={m} />)}
        </div>
      ) : next ? (
        <NextRow m={next} now={now} />
      ) : null}
    </div>
  );
}

function LiveRow({ m }: { m: EnrichedMatch }) {
  return (
    <Link
      to={`/matches/${m.id}`}
      className="card flex items-center gap-2 px-3 py-2 ring-1 ring-pitch-500/40 shadow-glow hover:border-pitch-500/60 transition-colors"
    >
      <span className="pill bg-pitch-500/15 text-pitch-400 shrink-0">
        <span className="size-1.5 rounded-full bg-pitch-400 animate-pulse" />
        LIVE {m.status_detail ?? ''}
      </span>
      <div className="flex-1 min-w-0 truncate text-sm">
        {m.home_flag ?? '🏳️'} {m.home_name} <span className="stat font-semibold">{m.home_score ?? 0}-{m.away_score ?? 0}</span> {m.away_name} {m.away_flag ?? '🏳️'}
      </div>
    </Link>
  );
}

function NextRow({ m, now }: { m: EnrichedMatch; now: number }) {
  const ms = new Date(m.utc_kickoff).getTime() - now;
  const close = ms < 60 * 60_000;
  const counter = formatCountdown(ms);
  return (
    <Link
      to={`/matches/${m.id}`}
      className={`card flex items-center gap-2 px-3 py-2 hover:border-pitch-500/40 transition-colors ${close ? 'ring-1 ring-pitch-500/30' : ''}`}
    >
      <span className="text-[10px] uppercase tracking-wider text-dim shrink-0">Next kickoff</span>
      <div className="flex-1 min-w-0 truncate text-sm">
        {m.home_flag ?? '🏳️'} {m.home_name} vs {m.away_name} {m.away_flag ?? '🏳️'}
      </div>
      <div className="text-right shrink-0">
        <div className={`stat text-sm font-semibold ${close ? 'text-pitch-400' : ''}`}>{counter}</div>
        <div className="text-[10px] text-dim">{rigaTime(m.utc_kickoff)}</div>
      </div>
    </Link>
  );
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'soon';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  return `${secs}s`;
}
